import {
    api,
    apiErrorMessage,
    businessDayLabel,
    useAuthStore,
} from '@bemor/web-kit';
import { Button, Field, Section, TimezoneSelect } from '@bemor/web-kit/ui';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import type { StaffProfile } from '@/api/types';

/**
 * BM-143 (decision 2): the staff member's own time zone, the one Admin shows dates and reads date filters in. Blank
 * follows the browser; with neither, the tenant's business zone. On a tenant board the business zone is shown beside
 * it, read-only: it is set on the platform's tenant page.
 */
export function TimeZoneSection() {
    const session = useAuthStore((s) => s.session);
    const tenantZone = useAuthStore((s) => s.tenant?.timezone ?? null);
    const saved = session?.actor.timezone ?? null;
    const [zone, setZone] = useState<string | null>(saved);
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState(false);
    const save = useMutation({
        mutationFn: async (value: string | null) =>
            (
                await api().patch<StaffProfile>('/staff/profile', {
                    timezone: value,
                })
            ).data,
        onSuccess: (profile) => {
            const current = useAuthStore.getState().session;
            if (current)
                useAuthStore.setState({
                    session: {
                        ...current,
                        actor: {
                            ...current.actor,
                            timezone: profile.timezone ?? undefined,
                        },
                    },
                });
            // the stored name (a legacy alias saves as its name today)
            setZone(profile.timezone ?? null);
            setDone(true);
        },
        onError: (e) => setError(apiErrorMessage(e)),
    });
    // the boot's browser zone (the bemor_tz cookie), the same on the server and in the hydrating browser
    const browser = useAuthStore((s) => s.browserTimezone ?? null);
    return (
        <Section title="Time zone" testId="security-time-zone">
            <form
                method="post"
                className="grid grid-cols-1 gap-3 md:grid-cols-2"
                onSubmit={(e) => {
                    e.preventDefault();
                    setError(null);
                    setDone(false);
                    save.mutate(zone);
                }}
            >
                <Field
                    id="profile-timezone"
                    label="Your time zone"
                    hint={`Your view: dates and date filters in Admin. Blank = your browser's${browser ? ` (${browser})` : ''}.`}
                >
                    <TimezoneSelect
                        id="profile-timezone"
                        value={zone}
                        onChange={(v) => {
                            setZone(v);
                            setDone(false);
                        }}
                        label="Your time zone"
                        placeholder="Follow my browser"
                        clearable
                        testId="profile-timezone-select"
                    />
                </Field>
                {tenantZone ? (
                    <Field
                        id="business-timezone"
                        label="Business time zone"
                        hint={`Periods are cut on ${businessDayLabel(tenantZone)}; set on the platform's tenant page.`}
                    >
                        <p
                            id="business-timezone"
                            className="text-fg-2 m-0 py-2 text-sm"
                            data-testid="business-timezone"
                        >
                            {tenantZone}
                        </p>
                    </Field>
                ) : null}
                <div className="flex items-center gap-3 md:col-span-2">
                    <Button
                        type="submit"
                        size="sm"
                        disabled={zone === saved || save.isPending}
                    >
                        Save time zone
                    </Button>
                    {done ? (
                        <p className="text-fg-3 m-0 text-xs" role="status">
                            Saved.
                        </p>
                    ) : null}
                    {error ? (
                        <p
                            role="alert"
                            className="text-destructive m-0 text-xs"
                        >
                            {error}
                        </p>
                    ) : null}
                </div>
            </form>
        </Section>
    );
}
